import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Save } from "lucide-react";
import { adminGetConfig, adminSaveConfig } from "../../riskprofiler/riskApi";

const inputCls = "w-full bg-white border border-oth-line rounded-xl px-3 py-2 font-body text-sm focus:outline-none focus:border-oth-blue";

export function RiskProfileSettingsTab() {
    const [cfg, setCfg] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => { try { setCfg(await adminGetConfig()); } catch { toast.error("Could not load configuration."); } };
    useEffect(() => { load(); }, []);

    if (!cfg) return <div className="font-editorial italic text-oth-ink3 py-10" data-testid="rp-settings-loading">Loading…</div>;

    const setBand = (i, key, val) => setCfg({ ...cfg, profiles: cfg.profiles.map((b, idx) => (idx === i ? { ...b, [key]: key === "min" || key === "max" ? (val === "" ? "" : Number(val)) : val } : b)) });

    const autoFit = () => setCfg((prev) => ({
        ...prev,
        profiles: prev.profiles.map((b, idx) => ({
            ...b,
            ...(idx === 0 ? { min: prev.current_min } : {}),
            ...(idx === prev.profiles.length - 1 ? { max: prev.current_max } : {}),
        })),
    }));

    const save = async () => {
        setBusy(true);
        try {
            await adminSaveConfig({ profiles: cfg.profiles.map((b) => ({ name: b.name, min: Number(b.min), max: Number(b.max), description: b.description || "" })), disclaimer: cfg.disclaimer || "" });
            toast.success("Profile configuration saved.");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not save configuration."); }
        finally { setBusy(false); }
    };

    return (
        <div data-testid="rp-settings-tab" className="space-y-6">
            {!cfg.coverage_ok && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 flex items-start gap-3 text-sm font-body text-[#92400e]" data-testid="rp-coverage-warning">
                    <AlertTriangle size={18} className="shrink-0 mt-0.5" />
                    <div>There are <strong>{cfg.active_question_count}</strong> active questions, so the possible score is <strong>{cfg.current_min}–{cfg.current_max}</strong>. Your current ranges don't cover this exactly — update the bands below so they span {cfg.current_min} to {cfg.current_max} with no gaps or overlaps.
                        <button onClick={autoFit} className="ml-2 underline font-medium hover:text-[#b45309]" data-testid="rp-autofit">Auto-fit the first/last band</button>
                    </div>
                </div>
            )}
            <div className="rounded-2xl border border-oth-line/70 bg-white p-5">
                <div className="text-xs uppercase tracking-[0.18em] text-oth-ink3 font-body mb-4">Profile bands — must cover {cfg.current_min}–{cfg.current_max}, no gaps, no overlaps</div>
                <div className="space-y-4">
                    {cfg.profiles.map((b, i) => (
                        <div key={i} className="rounded-2xl border border-oth-line/70 p-4" data-testid={`rp-band-${i}`}>
                            <div className="grid grid-cols-1 sm:grid-cols-[1fr_90px_90px] gap-3 items-end">
                                <label className="block">
                                    <div className="text-[11px] uppercase tracking-[0.16em] text-oth-ink3 font-body mb-1.5">Profile name</div>
                                    <input className={inputCls} value={b.name} onChange={(e) => setBand(i, "name", e.target.value)} data-testid={`rp-band-name-${i}`} />
                                </label>
                                <label className="block">
                                    <div className="text-[11px] uppercase tracking-[0.16em] text-oth-ink3 font-body mb-1.5">Min</div>
                                    <input type="number" className={inputCls} value={b.min} onChange={(e) => setBand(i, "min", e.target.value)} data-testid={`rp-band-min-${i}`} />
                                </label>
                                <label className="block">
                                    <div className="text-[11px] uppercase tracking-[0.16em] text-oth-ink3 font-body mb-1.5">Max</div>
                                    <input type="number" className={inputCls} value={b.max} onChange={(e) => setBand(i, "max", e.target.value)} data-testid={`rp-band-max-${i}`} />
                                </label>
                            </div>
                            <label className="block mt-3">
                                <div className="text-[11px] uppercase tracking-[0.16em] text-oth-ink3 font-body mb-1.5">Description</div>
                                <textarea className={`${inputCls} min-h-[64px]`} value={b.description || ""} onChange={(e) => setBand(i, "description", e.target.value)} data-testid={`rp-band-desc-${i}`} />
                            </label>
                        </div>
                    ))}
                </div>
            </div>
            <label className="block rounded-2xl border border-oth-line/70 bg-white p-5">
                <div className="text-xs uppercase tracking-[0.18em] text-oth-ink3 font-body mb-2">Disclaimer</div>
                <textarea className={`${inputCls} min-h-[90px]`} value={cfg.disclaimer || ""} onChange={(e) => setCfg({ ...cfg, disclaimer: e.target.value })} data-testid="rp-disclaimer" />
            </label>
            <button onClick={save} disabled={busy} className="btn-pill btn-pill-dark text-sm disabled:opacity-60" data-testid="rp-settings-save"><Save size={14} /> {busy ? "Saving…" : "Save configuration"}</button>
        </div>
    );
}
